import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { createCaslGuard } from '../../lib/auth/permissionGuards';
import { lockRouteBeforeLoad } from '@/lib/auth/resolveLockRedirect';
import { organizationQueryOptions } from '@/api/queries/useOrganization';
import { unresolvedDepartmentsQueryOptions } from '@/api/queries/useInitializeUnresolvedDepartments';
import { departmentsQueryOptions } from '@/api/queries/useDepartments';
import { orgMembersQueryOptions } from '@/api/queries/useOrgMembers';
import { orgFunctionsQueryOptions } from '@/api/queries/useOrgFunctions';
import { InitialSetupWizardPage } from '@/pages/initial-setup/InitialSetupWizardPage';
import { WizardIntroSkeleton } from '@/pages/initial-setup/components/WizardIntroSkeleton';
import { toWizardProgress } from '@/pages/initial-setup/useWizardProgress';
import { deriveResumeStep, WIZARD_STEP_IDS } from '@/pages/initial-setup/wizardSteps';

const initialSetupSearchSchema = z.object({
  // No step in the URL = resume at the first step needing attention — the
  // loader pins it into the URL (see below).
  step: z.enum(WIZARD_STEP_IDS).optional().catch(undefined)
});

export const Route = createFileRoute('/$slug/initial-setup')({
  component: InitialSetupWizardPage,
  // Every `?step` change is a new match whose loader resolves from cache, so
  // the router's default delay must stay and no minimum may hold the skeleton.
  pendingComponent: WizardIntroSkeleton,
  pendingMinMs: 0,
  validateSearch: initialSetupSearchSchema,
  loaderDeps: ({ search }) => ({ step: search.step }),
  // Key on the same InitialSetup.read grant the $slug parent uses to route
  // into the wizard, so a widened grant can never loop $slug ⇄ initial-setup.
  // The lock bounce runs after it, like the other two lock routes: the wizard
  // is a lock screen, so a completed org (or one locked by a seat overrun
  // instead) must not be able to re-enter it.
  beforeLoad: async (args) => {
    const casl = await createCaslGuard('InitialSetup', 'read')(args);
    await lockRouteBeforeLoad('/$slug/initial-setup')(args);
    return casl;
  },
  loader: async ({ context, params, deps }) => {
    // Progress derives from the first two queries; the other three feed
    // useAnchorSeedRows — ensured here so a done anchor step neither flashes
    // its empty state on a cold cache nor freezes an empty seed baseline.
    const [organization, unresolvedDepartments, departments] = await Promise.all([
      context.queryClient.ensureQueryData(organizationQueryOptions),
      context.queryClient.ensureQueryData(unresolvedDepartmentsQueryOptions),
      context.queryClient.ensureQueryData(departmentsQueryOptions()),
      context.queryClient.ensureQueryData(
        orgMembersQueryOptions({
          status: ['inactive', 'pending', 'active'],
          classification: 'internal'
        })
      ),
      context.queryClient.ensureQueryData(orgFunctionsQueryOptions('role'))
    ]);

    // Pin the resume step into the URL once, instead of re-deriving it every
    // render: an autosave flush flips progress stamps mid-edit, and a
    // render-derived step would then switch screens under the user.
    if (!deps.step) {
      const resolvedDepartments = departments.filter((department) => !!department.manager).length;
      throw redirect({
        to: '/$slug/initial-setup',
        params,
        search: {
          step: deriveResumeStep(
            toWizardProgress(organization, unresolvedDepartments.length, resolvedDepartments)
          )
        },
        replace: true
      });
    }
  }
});
